import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { mutateImageUrl } from '../../wordpress';
import EmblaCarousel from 'embla-carousel';

/**
 * Slideshow: new and improved v2
 *
 * @author Matt Haynes <jmatthew.haynes@joipolloi.com>
 * @package Minifigs 2.0
 */

const Slideshow = ({ images = [], attr, videoUrls = [] }) => {
    const viewportRef = useRef(null);
    const emblaRef = useRef(null);
    const [selectedIndex, setSelectedIndex] = useState(0);
    const slideContent = [...images, ...videoUrls];

    useEffect(() => {
        if (!viewportRef.current) return;
        emblaRef.current = EmblaCarousel(viewportRef.current, {
            loop: false,
            draggable: true,
            align: 'start',
        });

        const embla = emblaRef.current;
        const onSelect = () => setSelectedIndex(embla.selectedScrollSnap());
        embla.on('select', onSelect);
        onSelect();
        return () => embla.destroy();
    }, [slideContent.length]);

    useEffect(() => {
        const embla = emblaRef.current;
        if (!embla) return;
        if (attr?.image?.url && images.length) {
            const attrUrl = attr.image.url;
            const imageIndex = images.findIndex((image) => image.node.url === attrUrl);
            if (imageIndex > -1) embla.scrollTo(imageIndex, true);
        }
    }, [images, attr]);

    const scrollTo = (index) => {
        const embla = emblaRef.current;
        if (!embla) return;
        embla.scrollTo(index);
    };

    return (
        <div className="product-media">
            {slideContent?.length > 0 && (
                <div className="slideshow-images">
                    <div className="swiper-product-media-gallery embla-product-media">
                        <div className="embla__viewport" ref={viewportRef}>
                            <ul className="embla__container">
                                {slideContent.map((slide, idx) => (
                                    <li className="embla__slide" key={idx} data-index={idx}>
                                        {slide.format === 'mp4' ? (
                                            <video
                                                src={slide.url}
                                                className="video-player"
                                                autoPlay
                                                playsInline
                                                muted
                                                loop
                                                controls={false}
                                            >
                                                <source src={slide.url} type="video/mp4" />
                                                Your browser does not support the video tag.
                                            </video>
                                        ) : (
                                            <img src={mutateImageUrl(slide.node.url, 400, 400)} alt="" />
                                        )}
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                    <ul
                        className="swiper-product-media-thumbs product-media-thumbs-dots"
                        role="tablist"
                        aria-label="Media thumbnails"
                    >
                        {slideContent.map((slide, idx) => {
                            const isActive = idx === selectedIndex;
                            const isVideo = slide.format === 'mp4';
                            return (
                                <li key={idx} className="thumb-dot-wrapper" role="presentation">
                                    <button
                                        type="button"
                                        className={`thumb-dot${isActive ? ' is-active' : ''}${
                                            isVideo ? ' is-video' : ''
                                        }`}
                                        aria-label={`Go to ${isVideo ? 'video' : 'image'} ${idx + 1}`}
                                        aria-selected={isActive}
                                        role="tab"
                                        onClick={() => scrollTo(idx)}
                                    >
                                        {isVideo && (
                                            <span className="thumb-dot__play" aria-hidden="true">
                                                ▶
                                            </span>
                                        )}
                                    </button>
                                </li>
                            );
                        })}
                    </ul>
                </div>
            )}
        </div>
    );
};

Slideshow.propTypes = {
    images: PropTypes.array.isRequired,
    videoUrls: PropTypes.array,
    attr: PropTypes.shape({
        image: PropTypes.shape({
            url: PropTypes.string,
        }),
    }),
};

export default Slideshow;
